import {
  ZAvatar, OutlinedIcons, useCurrentTime,
} from '@marageti/z4-lib';
import { PersonType, Traveler } from '@marageti/z4-sdk/lib/people';
import {
  GetParticipantsResponse, ParticipantInfo,
  TripMatch,
} from '@marageti/z4-sdk/lib/travel';
import {
  Box, Button, Card, CardHeader, Divider, Stack, Typography,
} from '@mui/material';
import React from 'react';
import { RoleName, UserStatus } from '@marageti/z4-sdk/lib/auth';

const getFullName = (participant: ParticipantInfo): string => [participant.givenName, participant.familyName].filter(Boolean).join(' ');

const getParticipantTitle = (participant: ParticipantInfo, primaryTravelerId: string): string => {
  if (participant.personType === PersonType.Traveler) {
    if (participant.id === primaryTravelerId) {
      return 'Primary Traveler';
    }
    return 'Co-Traveler';
  }

  if (participant.personType === PersonType.Agent) {
    switch (participant.userInfo?.[0]?.roles?.[0]) {
      case RoleName.AGENT_SUPPORT:
        return 'Support';

      case RoleName.AGENT:
      case RoleName.AGENT_ADMIN:
      default:
        return 'Travel Specialist';
    }
  }

  return '';
};

const {
  OutlinedClockIcon,
} = OutlinedIcons;

const Section = ({
  'data-e2e': e2e = undefined,
  title = undefined,
  children = undefined,
}: {
  ['data-e2e']?: string,
  title?: React.ReactNode,
  children?: React.ReactNode,
}) => (
  <Box sx={{ mb: 0 }} data-e2e={e2e}>
    {title && <Typography variant="h4" sx={{ mb: 2 }}>{title}</Typography>}
    {children}
  </Box>
);

const ParticipantItem = ({
  fullName,
  title,
  timeZone,
  isPending = false,
  avatar,
}: {
  fullName: string,
  title: string,
  timeZone: string,
  isPending: boolean,
  avatar: string | undefined,
}) => {
  const currentTime = useCurrentTime(timeZone);

  return (
    <CardHeader
      sx={{ opacity: isPending ? 0.4 : 1 }}
      avatar={(
        <ZAvatar size={40} src={avatar} name={fullName} />
      )}
      title={fullName + (isPending ? ' (Pending Confirmation)' : '')}
      subheader={(
        <Stack
          direction="row"
          sx={{ alignItems: 'center' }}
        >
          {title}
          {' '}
          {currentTime[0] && (
            <OutlinedClockIcon
              sx={{
                fontSize: '0.8rem',
                ml: 1,
                mr: 0.5,
              }}
            />
          )}
          {currentTime[0]}
        </Stack>
                )}
      titleTypographyProps={{
        variant: 'body1',
        fontWeight: 'bold',
      }}
    />
  );
};

interface ParticipantsCardProps {
  tripMatch: TripMatch | undefined;
  loggedInTraveler: Traveler | null
  participants: GetParticipantsResponse | undefined;
  canInviteCoTravelers?: boolean;
  onInviteCoTravelers?: () => void;
}

const ParticipantsCard = ({
  tripMatch,
  loggedInTraveler,
  participants,
  canInviteCoTravelers = false,
  onInviteCoTravelers = () => {},
}: ParticipantsCardProps) => {
  const primaryTraveler = tripMatch?.travelers[0];
  const assignedAgentParticipant = participants && tripMatch ? participants[tripMatch?.assignedTo || ''] : undefined;
  const participantEntries = participants ? Object.values(participants).filter((participant) => participant.id !== tripMatch?.assignedTo) : [];

  const sortedParticipantEntries = participantEntries.sort((aParticipant, bParticipant) => {
    // Sort agents before travelers
    if (aParticipant.personType !== bParticipant.personType) {
      if (aParticipant.personType === PersonType.TeamMember) return -1;
      if (bParticipant.personType === PersonType.TeamMember) return 1;
      return aParticipant.personType === PersonType.Traveler ? 1 : -1;
    }

    // Sort alphabetically by given name
    return aParticipant.givenName.localeCompare(bParticipant.givenName);
  });
  // Prepend the assigned agent
  if (assignedAgentParticipant) sortedParticipantEntries.unshift(assignedAgentParticipant);

  if (participantEntries.length === 0) return null;

  return (
    <Section>
      <Card>
        <Stack>
          {sortedParticipantEntries.map((participant) => {
            // Don't include the logged in user in the list of participants
            if (participant.id === loggedInTraveler?.id) return null;
            const fullName = getFullName(participant);

            return (
              <ParticipantItem
                key={participant.id}
                avatar={participant.avatar}
                title={getParticipantTitle(participant, primaryTraveler?.id || '')}
                fullName={fullName}
                timeZone={participant.timeZone?.name || ''}
                isPending={participant.userInfo?.[0]?.status === UserStatus.PENDING}
              />
            );
          })}

        </Stack>

        <Stack
          sx={{
            pt: 0, pr: 3, pb: 3, pl: 3,
          }}
          gap={3}
        >
          <Divider />
          <Typography variant="caption">
            Always communicate through this messaging platform to ensure you&apos;re covered by our terms of service, privacy policy and other safeguards.
          </Typography>
          {canInviteCoTravelers && (
            <Button
              variant="outlined"
              size="small"
              sx={{
                width: '100%', borderColor: '#C2C5C5', marginTop: 1, backgroundColor: '#FFF',
              }}
              onClick={onInviteCoTravelers}
            >
              <Typography variant="captionBold" color="primary">Invite Co-Travelers</Typography>
            </Button>
          )}
        </Stack>

      </Card>
    </Section>
  );
};

export default ParticipantsCard;
